import { useState, useEffect } from "react";
import { Plus, Trash2, Edit, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    max_guests: 5,
    base_price: 150,
    extra_person_price: 25,
    amenities: '',
    images: '',
    video_url: '',
    latitude: 50.82012,
    longitude: -0.13742
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseFloat(value) : value
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      location: '',
      max_guests: 5,
      base_price: 150,
      extra_person_price: 25,
      amenities: '',
      images: '',
      video_url: '',
      latitude: 50.82012,
      longitude: -0.13742
    });
    setEditingProperty(null);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      description: property.description,
      location: property.location,
      max_guests: property.max_guests,
      base_price: property.base_price,
      extra_person_price: property.extra_person_price,
      amenities: property.amenities.join(', '),
      images: property.images.join('\n'),
      video_url: property.video_url || '',
      latitude: property.latitude,
      longitude: property.longitude
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const propertyData = {
      name: formData.name,
      description: formData.description,
      location: formData.location,
      max_guests: parseInt(formData.max_guests),
      base_price: parseFloat(formData.base_price),
      extra_person_price: parseFloat(formData.extra_person_price),
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
      images: formData.images.split('\n').map(i => i.trim()).filter(i => i),
      video_url: formData.video_url || null,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude)
    };
    
    try {
      if (editingProperty) {
        // For editing, we'd need an update endpoint - for now, delete and recreate
        await axios.delete(`${API}/properties/${editingProperty.id}`);
      }
      
      await axios.post(`${API}/properties`, propertyData);
      toast.success(editingProperty ? "Property updated successfully!" : "Property added successfully!");
      setIsDialogOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error("Error saving property:", error);
      toast.error("Failed to save property");
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property? This will also delete all associated bookings.')) return;
    
    try {
      await axios.delete(`${API}/properties/${propertyId}`);
      toast.success("Property deleted");
      fetchProperties();
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-properties">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">
              Manage Properties
            </h1>
            <p className="text-slate-600 mt-2">Add and manage all your rental properties with independent pricing and details</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90" data-testid="add-property-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="property-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name">Property Title *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Stylish 2-Bed Brighton Retreat"
                      data-testid="property-name-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      required
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Full property description..."
                      rows={6}
                      data-testid="property-description-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="location">Full Address *</Label>
                    <Input
                      id="location"
                      name="location"
                      required
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., 24 Green Diamond, Bartholomew, Brighton, BN1 1JS"
                      data-testid="property-location-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="latitude">Latitude *</Label>
                    <Input
                      id="latitude"
                      name="latitude"
                      type="number"
                      step="0.000001"
                      required
                      value={formData.latitude}
                      onChange={handleInputChange}
                      data-testid="property-latitude-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="longitude">Longitude *</Label>
                    <Input
                      id="longitude"
                      name="longitude"
                      type="number"
                      step="0.000001"
                      required
                      value={formData.longitude}
                      onChange={handleInputChange}
                      data-testid="property-longitude-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="max_guests">Max Guests *</Label>
                    <Input
                      id="max_guests"
                      name="max_guests"
                      type="number"
                      min="1"
                      required
                      value={formData.max_guests}
                      onChange={handleInputChange}
                      data-testid="property-max-guests-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="base_price">Base Price (£) *</Label>
                    <Input
                      id="base_price"
                      name="base_price"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.base_price}
                      onChange={handleInputChange}
                      data-testid="property-base-price-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Per night for 1-2 guests</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="extra_person_price">Extra Person Price (£) *</Label>
                    <Input
                      id="extra_person_price"
                      name="extra_person_price"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.extra_person_price}
                      onChange={handleInputChange}
                      data-testid="property-extra-price-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Per additional guest per night</p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="amenities">Amenities (comma separated) *</Label>
                    <Input
                      id="amenities"
                      name="amenities"
                      required
                      value={formData.amenities}
                      onChange={handleInputChange}
                      placeholder="WiFi, TV, Kitchen, Parking, etc."
                      data-testid="property-amenities-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="images">Image URLs (one per line) *</Label>
                    <Textarea
                      id="images"
                      name="images"
                      required
                      value={formData.images}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                      rows={4}
                      data-testid="property-images-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="video_url">Video URL (optional)</Label>
                    <Input
                      id="video_url"
                      name="video_url"
                      value={formData.video_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/video.mp4"
                      data-testid="property-video-input"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-primary" data-testid="submit-property-button">
                    {editingProperty ? 'Update Property' : 'Add Property'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {properties.map((property, index) => (
            <Card key={property.id} data-testid={`property-item-${index}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{property.name}</CardTitle>
                    <p className="text-sm text-slate-600">{property.location}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEdit(property)}
                      data-testid={`edit-property-${index}`}
                    >
                      <Edit className="w-4 h-4 text-primary" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(property.id)}
                      data-testid={`delete-property-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <img 
                  src={property.images[0]} 
                  alt={property.name}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 line-clamp-3">{property.description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs bg-secondary text-primary px-2 py-1 rounded">
                      Up to {property.max_guests} guests
                    </span>
                    <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded font-medium">
                      £{property.base_price}/night
                    </span>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                      +£{property.extra_person_price} per extra guest
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {property.amenities.slice(0, 4).map((amenity, idx) => (
                      <span key={idx} className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        {amenity}
                      </span>
                    ))}
                    {property.amenities.length > 4 && (
                      <span className="text-xs text-slate-500">+{property.amenities.length - 4} more</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {properties.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No properties added yet.</p>
              <p className="text-sm text-slate-500 mb-6">Add your first property to start receiving bookings.</p>
              <Button onClick={() => setIsDialogOpen(true)}>Add Property</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;
